import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import { storageGet, storageSet } from "../utils/browserSafety";

const ModeContext = createContext(null);

export const useMode = () => {
  const ctx = useContext(ModeContext);
  if (!ctx) throw new Error("useMode must be used within ModeProvider");
  return ctx;
};

export const ModeProvider = ({ children }) => {
  // Init: use saved pref -> otherwise system pref -> fallback false
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window === "undefined") return false;
    const saved = storageGet("darkMode");
    if (saved !== null) return saved === "true";
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  });

  // Apply to <html> for Tailwind’s `dark` variant and persist
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", darkMode);
    storageSet("darkMode", darkMode ? "true" : "false");
  }, [darkMode]);

  // Keep in sync with system changes if user hasn’t chosen explicitly
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => {
      const saved = storageGet("darkMode");
      if (saved === null) setDarkMode(e.matches);
    };
    // Safari < 14 only supports the deprecated addListener/removeListener.
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", handler);
      return () => media.removeEventListener("change", handler);
    }
    media.addListener(handler);
    return () => media.removeListener(handler);
  }, []);

  const toggle = useCallback(() => setDarkMode((v) => !v), []);
  const value = useMemo(
    () => ({ darkMode, setDarkMode, toggle }),
    [darkMode, toggle]
  );

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
};
